import type { IconName } from "./components/Icon";
import type { Layout } from "./session";

/**
 * The navigation for each of the four layouts.
 *
 * The rule StrategyLab's Starter/Pro split follows holds here too: a layout
 * hides tools, never data. A guest reads everything public; Starter adds
 * building and a single bot; Pro adds the research tools; Admin adds the
 * console. Where a page is not built yet the route still exists and says
 * which milestone brings it — a nav that grows by adding links, not by
 * rearranging them.
 */
export interface NavLink {
  to: string;
  label: string;
  hint?: string;
  end?: boolean;
  /** Other paths that belong under this entry, so it stays highlighted on them. */
  also?: string[];
  /** Drawn before the label (the label always shows). */
  icon?: IconName;
}
export interface NavGroup {
  id: string;
  label: string;
  hint: string;
  icon?: IconName;
  children: NavLink[];
}
export type NavItem = NavLink | NavGroup;

export function isGroup(item: NavItem): item is NavGroup {
  return "children" in item;
}

const EXPLORE: NavGroup = {
  id: "explore",
  label: "Explore",
  hint: "What the community is building and running",
  icon: "explore",
  children: [
    { to: "/explore", label: "Strategies", hint: "Published, rated, forkable", icon: "strategies" },
    {
      to: "/explore/bots",
      label: "Bots",
      hint: "Public paper bots, ranked, with their drawdown",
      icon: "bots",
    },
    { to: "/benchmarks", label: "Benchmarks", hint: "Published results you can re-run", icon: "benchmark" },
    { to: "/league", label: "League", hint: "Every public bot competes, month by month", icon: "league" },
  ],
};

const RESEARCH: NavGroup = {
  id: "research",
  label: "Research",
  hint: "Pro tools for a strategy you already believe in",
  icon: "research",
  children: [
    { to: "/optimize", label: "Optimize", hint: "Sweep settings on one market", icon: "optimize" },
    {
      to: "/walkforward",
      label: "Walk-forward",
      hint: "Train, then test on unseen data",
      icon: "walkforward",
    },
    { to: "/robustness", label: "Robustness", hint: "One strategy across every market", icon: "robustness" },
    { to: "/benchmark", label: "Benchmark", hint: "Many strategies, one market", icon: "benchmark" },
    {
      to: "/market-data",
      label: "Market data",
      hint: "What StrategyHive's hosted candles cover",
      icon: "markets",
    },
  ],
};

const BUILD: NavItem[] = [
  // One entry for everything of yours; strategies, backtests and bots are tabs inside it.
  // Your bots used to have their own entry too; public ones are under Explore.
  { to: "/me", label: "Dashboard", end: true, also: ["/strategies", "/history", "/bots"], icon: "space" },
  { to: "/workbench", label: "Workbench", icon: "workbench" },
];

/** Always the end of the bar: Download, then Docs last. */
const COMMON_TAIL: NavLink[] = [
  {
    to: "/download",
    label: "Download",
    hint: "The desktop app, or install the website as an app",
    icon: "download",
  },
  { to: "/docs", label: "Docs", icon: "docs" },
];

export function navFor(layout: Layout): NavItem[] {
  switch (layout) {
    case "guest":
      return [EXPLORE, { to: "/market-data", label: "Markets", icon: "markets" }, ...COMMON_TAIL];
    case "starter":
      return [...BUILD, EXPLORE, ...COMMON_TAIL];
    case "pro":
      return [...BUILD, RESEARCH, EXPLORE, ...COMMON_TAIL];
    case "admin":
      return [...BUILD, RESEARCH, EXPLORE, { to: "/admin", label: "Admin", icon: "admin" }, ...COMMON_TAIL];
  }
}

export const LAYOUT_NAME: Record<Layout, string> = {
  guest: "Guest",
  starter: "Starter",
  pro: "Pro",
  admin: "Admin",
};
